import { useAccountInfo, useAccountLines } from '@/src/hooks';
import { Token } from '@/src/models';
import { useMemo } from 'react';
import { dropsToXrp } from 'xrpl';

type UseTokenBalanceProps = {
  token: Token | undefined;
  /** XRPL account address */
  address: string | undefined;
};

/**
 * Custom hook to fetch and format token balances for XRPL accounts.
 *
 * Automatically handles both XRP and non-XRP tokens by:
 * - Using `useAccountInfo` for XRP balances (converts drops to XRP)
 * - Using `useAccountLines` for token balances (trust lines)
 *
 * @param token - The token to get balance for (undefined returns '0')
 * @param address - XRPL account address to query
 * @returns Formatted balance string (e.g., "123.45" for XRP, "1000.00" for tokens)
 *
 * @example
 * ```tsx
 * const xrpBalance = useTokenBalance({
 *   token: { code: 'XRP' },
 *   address: 'rN7n7otQDd6FczFgLdSqtcsAUxDkw6fzRH'
 * });
 * // Returns: "123.45"
 *
 * const usdcBalance = useTokenBalance({
 *   token: { code: 'USDC', issuer: 'rN7n7otQDd6FczFgLdSqtcsAUxDkw6fzRH' },
 *   address: 'rN7n7otQDd6FczFgLdSqtcsAUxDkw6fzRH'
 * });
 * // Returns: "1000.00"
 * ```
 */
export const useTokenBalance = ({ token, address }: UseTokenBalanceProps) => {
  const isXrp = token?.code === 'XRP';

  // Get account info if token is XRP
  const { data: accountInfo } = useAccountInfo(
    { account: address || '' },
    { enabled: !!address && isXrp }
  );

  // Get account lines if token is not XRP
  const { data: accountLines } = useAccountLines(
    { account: address || '' },
    { enabled: !!address && !isXrp }
  );

  // Get token balance
  const tokenBalance = useMemo(() => {
    if (!token) return '0';

    if (isXrp) {
      const balance = accountInfo?.account_data?.Balance;
      if (!balance) return '0';
      return dropsToXrp(balance).toFixed(2);
    }

    // Handle non-XRP token balance
    if (!accountLines?.lines) return '0';
    // Match by both currency code AND issuer for accurate balance lookup
    const matchingLine = accountLines.lines.find((line) =>
      line.currency === token.code && line.account === token.issuer
    );
    return matchingLine?.balance ?? '0';
  }, [token, isXrp, accountInfo, accountLines]);

  return tokenBalance;
};
